import { mapContentBounds, renderMapScene } from "@/src/core/engine/Renderer";
import { floorForMode, hexesForMode, objectsForMode } from "@/src/core/mapMode";
import { t } from "@/src/i18n";
import type { MapDocument } from "@/src/types/map";

export interface PdfExportOptions {
  orientation: "portrait" | "landscape";
  dpi: 150 | 300;
  marginMm: number;
  overlapMm: number;
}

const pageSize = (orientation: PdfExportOptions["orientation"]) =>
  orientation === "portrait"
    ? { width: 210, height: 297 }
    : { width: 297, height: 210 };

export const estimatePdfPages = (map: MapDocument, options: PdfExportOptions) => {
  const page = pageSize(options.orientation);
  const geometry = floorForMode(map.geometry.floorPolygons, map.meta.gridType);
  const hexCells = hexesForMode(map.geometry.hexCells ?? [], map.meta.gridType);
  const objects = objectsForMode(map.objects, map.meta.gridType);
  const patches = map.meta.gridType === "square" ? map.geometry.terrainPatches ?? [] : [];
  const bounds = mapContentBounds(
    geometry,
    objects,
    hexCells,
    map.meta.gridSizeWorld,
    patches,
  );
  const worldToMm = map.meta.printGridSizeMm / map.meta.gridSizeWorld;
  const paddingWorld = map.meta.gridSizeWorld * 0.75;
  const contentWidthMm = (bounds.maxX - bounds.minX + paddingWorld * 2) * worldToMm;
  const contentHeightMm = (bounds.maxY - bounds.minY + paddingWorld * 2) * worldToMm;
  const printableWidth = page.width - options.marginMm * 2;
  const printableHeight = page.height - options.marginMm * 2;
  const stepWidth = Math.max(20, printableWidth - options.overlapMm);
  const stepHeight = Math.max(20, printableHeight - options.overlapMm);
  const columns = Math.max(1, Math.ceil((contentWidthMm - options.overlapMm) / stepWidth));
  const rows = Math.max(1, Math.ceil((contentHeightMm - options.overlapMm) / stepHeight));
  return { columns, rows, total: columns * rows };
};

const safeFilename = (title: string) =>
  title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "dungeon-map";

export const exportTiledPdf = async (
  map: MapDocument,
  options: PdfExportOptions,
  onProgress?: (current: number, total: number) => void,
) => {
  const { jsPDF } = await import("jspdf");
  const page = pageSize(options.orientation);
  const pages = estimatePdfPages(map, options);
  if (pages.total > 48) {
    throw new Error(t("export.tooManyPages", { count: pages.total }));
  }

  const geometry = floorForMode(map.geometry.floorPolygons, map.meta.gridType);
  const hexCells = hexesForMode(map.geometry.hexCells ?? [], map.meta.gridType);
  const objects = objectsForMode(map.objects, map.meta.gridType);
  const patches = map.meta.gridType === "square" ? map.geometry.terrainPatches ?? [] : [];
  const bounds = mapContentBounds(
    geometry,
    objects,
    hexCells,
    map.meta.gridSizeWorld,
    patches,
  );
  const worldToMm = map.meta.printGridSizeMm / map.meta.gridSizeWorld;
  const paddingWorld = map.meta.gridSizeWorld * 0.75;
  const originX = bounds.minX - paddingWorld;
  const originY = bounds.minY - paddingWorld;
  const printableWidth = page.width - options.marginMm * 2;
  const printableHeight = page.height - options.marginMm * 2;
  const stepWorldX = (printableWidth - options.overlapMm) / worldToMm;
  const stepWorldY = (printableHeight - options.overlapMm) / worldToMm;
  const pixelsPerMm = options.dpi / 25.4;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(page.width * pixelsPerMm);
  canvas.height = Math.round(page.height * pixelsPerMm);
  const context = canvas.getContext("2d");
  if (!context) throw new Error(t("export.canvasFailed"));

  const pdf = new jsPDF({
    orientation: options.orientation,
    unit: "mm",
    format: "a4",
    compress: true,
  });
  pdf.setProperties({
    title: map.meta.title,
    subject: map.meta.gridType === "hex" ? t("export.pdfSubjectHex") : t("export.pdfSubjectDungeon"),
    creator: "Dungeon Builder",
  });

  let index = 0;
  for (let row = 0; row < pages.rows; row += 1) {
    for (let column = 0; column < pages.columns; column += 1) {
      if (index > 0) pdf.addPage("a4", options.orientation);
      const worldX = originX + column * stepWorldX;
      const worldY = originY + row * stepWorldY;
      renderMapScene({
        context,
        width: page.width,
        height: page.height,
        deviceScale: pixelsPerMm,
        viewport: {
          x: options.marginMm - worldX * worldToMm,
          y: options.marginMm - worldY * worldToMm,
          zoom: worldToMm,
        },
        meta: map.meta,
        geometry,
        hexCells,
        objects,
        terrainPatches: patches,
        showVignette: false,
      });

      context.setTransform(pixelsPerMm, 0, 0, pixelsPerMm, 0, 0);
      context.strokeStyle = "rgba(42, 34, 27, 0.7)";
      context.fillStyle = "rgba(42, 34, 27, 0.72)";
      context.lineWidth = 0.25;
      context.setLineDash([2, 1.5]);
      context.strokeRect(
        options.marginMm,
        options.marginMm,
        printableWidth,
        printableHeight,
      );
      context.setLineDash([]);
      context.font = "2.8px sans-serif";
      context.fillText(
        t("export.pageFooter", { title: map.meta.title, column: column + 1, columns: pages.columns, row: row + 1, rows: pages.rows }),
        options.marginMm,
        page.height - 3,
      );

      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.92),
        "JPEG",
        0,
        0,
        page.width,
        page.height,
        undefined,
        "FAST",
      );
      index += 1;
      onProgress?.(index, pages.total);
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    }
  }

  pdf.save(`${safeFilename(map.meta.title)}-a4.pdf`);
};
